import { Component, computed, inject, input, OnDestroy, OnInit, output, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PinCodeComponent } from '../pin-code/pin-code.component';
import { ButtonComponent } from '../button/button.component';
import { ModalComponent } from '../modal/modal.component';
import { ModalRef } from '../modal/modal-ref';
import { MODAL_DATA } from '../modal/modal-tokens';

export interface VerificationModalData {
  email: string;
  errorText: WritableSignal<string>;
  loading: WritableSignal<boolean>;
  initialCountdown: number;
  titleKey?: string;
  subtitleKey?: string;
  buttonKey?: string;
  onVerify: (code: string) => void;
  onResend: () => void;
}

@Component({
  selector: 'app-verification-modal',
  standalone: true,
  imports: [FormsModule, TranslateModule, PinCodeComponent, ButtonComponent, ModalComponent],
  templateUrl: './verification-modal.component.html',
  styleUrl: './verification-modal.component.scss',
})
export class VerificationModalComponent implements OnInit, OnDestroy {
  private modalData = inject(MODAL_DATA, { optional: true }) as VerificationModalData | null;
  private modalRef = inject(ModalRef, { optional: true });

  // Inputs (template-mode backward compat)
  email = input('example@mail.com');
  errorText = input('');
  loading = input(false);
  initialCountdown = input(60);
  titleKey = input('VERIFICATION.TITLE');
  subtitleKey = input('VERIFICATION.SUBTITLE');
  buttonKey = input('VERIFICATION.BUTTON');

  // Outputs (template-mode backward compat)
  close = output<void>();
  verify = output<string>();
  resend = output<void>();

  // Effective values — prefer MODAL_DATA, fall back to inputs
  readonly effectiveEmail = computed(() => this.modalData?.email ?? this.email());
  readonly effectiveErrorText = computed(() => this.modalData?.errorText() ?? this.errorText());
  readonly effectiveLoading = computed(() => this.modalData?.loading() ?? this.loading());
  readonly effectiveTitleKey = computed(() => this.modalData?.titleKey ?? this.titleKey());
  readonly effectiveSubtitleKey = computed(() => this.modalData?.subtitleKey ?? this.subtitleKey());
  readonly effectiveButtonKey = computed(() => this.modalData?.buttonKey ?? this.buttonKey());

  pinCode = signal('');
  countdown = signal(0);

  isVerifyDisabled = computed(() => this.pinCode().length < 6);
  isCountdownActive = computed(() => this.countdown() > 0);

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    const countdown = this.modalData?.initialCountdown ?? this.initialCountdown();
    this.startCountdown(countdown);
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  onPinChange(code: string): void {
    this.pinCode.set(code);
  }

  onVerify(): void {
    if (!this.isVerifyDisabled()) {
      const code = this.pinCode();
      if (this.modalData?.onVerify) {
        this.modalData.onVerify(code);
      } else {
        this.verify.emit(code);
      }
    }
  }

  onClose(): void {
    if (this.modalRef) {
      this.modalRef.close();
    } else {
      this.close.emit();
    }
  }

  onResend(): void {
    if (this.modalData?.onResend) {
      this.modalData.onResend();
    } else {
      this.resend.emit();
    }
    this.startCountdown(60);
  }

  startCountdown(seconds: number): void {
    this.clearCountdown();
    this.countdown.set(seconds);

    this.countdownInterval = setInterval(() => {
      const current = this.countdown();
      if (current <= 1) {
        this.countdown.set(0);
        this.clearCountdown();
      } else {
        this.countdown.set(current - 1);
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }
}
